/**
 * Super Admin Service
 * Handles all super admin API operations for platform-wide management
 */

import api from '@services/api';
import type { TenantContextPreviewResponse } from '@modules/group_admin/services/groupAdminService';

// ===== Platform Stats =====
interface PlatformStats {
  tenants: {
    total: number;
    active: number;
    trial: number;
    recent_7_days: number;
  };
  users: {
    total: number;
    active: number;
    admins: number;
    recent_7_days: number;
  };
  system: {
    status: string;
    uptime: string;
    last_updated: string;
  };
}

// ===== Tenants =====
interface Tenant {
  tenant_id: string;
  tenant_name: string;
  tenant_code: string;
  parent_tenant_id?: string | null;  // Parent tenant for hierarchy
  tenant_subdomain: string | null;
  tenant_domain: string | null;
  subscription_plan: string;
  tenant_status: string;
  depth?: number;  // Hierarchy depth (0 = root)
  is_company?: boolean;
  multi_user_login_allowed?: boolean;
  created_at: string;
  updated_at: string | null;
  user_count?: number;
  active_user_count?: number;
  tenant_settings?: {
    allowed_conversation_types?: string[];
    [key: string]: any;
  };
}

interface TenantsResponse {
  tenants: Tenant[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  filters: {
    status: string;
    search: string;
  };
}

interface CreateTenantPayload {
  name: string;
  code: string;
  subdomain?: string;
  subscription_tier?: string;
  status?: string;
  parent_tenant_id?: string;
}

// ===== Users =====
interface User {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  user_tier?: number;
  created_at: string;
  last_login_at: string | null;
  tenant: {
    tenant_id: string;
    tenant_name: string;
    tenant_code: string;
  };
}

interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  filters: {
    role: string;
    status: string;
    tenant: string;
    search: string;
  };
}

interface CreateUserPayload {
  tenant_id: string;
  email: string;
  full_name: string;
  role: string;
  password: string;
}

// ===== Batch User Upload =====
interface BatchUserUpload {
  tenant_name: string;
  email: string;
  password: string;
  full_name?: string;
  role?: 'user' | 'editor' | 'admin';
}

interface BatchUploadResult {
  success: boolean;
  total: number;
  successful_count: number;
  failed_count: number;
  successful: Array<{
    index: number;
    email: string;
    tenant_name: string;
    user_id?: string;
    full_name?: string;
    role?: string;
  }>;
  failed: Array<{
    index: number;
    email: string;
    tenant_name: string;
    error: string;
    error_code?: string;
  }>;
}

// ===== Analytics =====
interface PlatformAnalytics {
  period: string;
  tenant_growth: Array<{ date: string; count: number }>;
  user_growth: Array<{ date: string; count: number }>;
  top_tenants: Array<{ name: string; slug: string; user_count: number }>;
}

// ===== System Prompts =====
interface SystemPrompt {
  prompt_id: string;
  prompt_key: string;
  prompt_name: string;
  prompt_content: string;
  prompt_description: string | null;
  language_code: string;
  prompt_tags: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by_user_id: string | null;
  updated_by_user_id: string | null;
}

interface SystemPromptsResponse {
  system_prompts: SystemPrompt[];
  total: number;
}

interface CreateSystemPromptPayload {
  prompt_key: string;
  prompt_name: string;
  prompt_content: string;
  prompt_description?: string;
  language_code?: string;
  prompt_tags?: string[];
}

interface UpdateSystemPromptPayload {
  prompt_name?: string;
  prompt_content?: string;
  prompt_description?: string;
  language_code?: string;
  prompt_tags?: string[];
  is_active?: boolean;
}

class SuperAdminService {
  // ===== Platform Stats =====

  /**
   * Get platform-wide statistics
   */
  async getPlatformStats(): Promise<PlatformStats> {
    const { data } = await api.get('/super-admin/platform-stats');
    return data;
  }

  // ===== Tenant Management =====

  /**
   * Get all tenants
   */
  async getTenants(params?: {
    page?: number;
    per_page?: number;
    status?: string;
    search?: string;
  }): Promise<TenantsResponse> {
    const { data } = await api.get('/super-admin/tenants', { params });
    return data;
  }

  /**
   * Generate a unique tenant code from a name
   */
  async generateTenantCode(name: string): Promise<string> {
    const { data } = await api.post('/super-admin/tenants/generate-code', { name });
    return data.code;
  }

  /**
   * Check if a tenant code is available
   */
  async checkTenantCodeAvailable(code: string): Promise<boolean> {
    const { data } = await api.post('/super-admin/tenants/check-code', { code });
    return data.available;
  }

  /**
   * Create a new tenant
   */
  async createTenant(payload: CreateTenantPayload): Promise<Tenant> {
    const { data } = await api.post('/super-admin/tenants', payload);
    return data.tenant;
  }

  /**
   * Update tenant information
   */
  async updateTenant(
    tenantId: string,
    updates: Partial<Tenant>
  ): Promise<Tenant> {
    const { data } = await api.put(`/super-admin/tenants/${tenantId}`, updates);
    return data.tenant;
  }

  async setTenantAsCompany(tenantId: string, isCompany: boolean): Promise<{ is_company: boolean; descendants_updated: number }> {
    const { data } = await api.post(`/super-admin/tenants/${tenantId}/set-company`, { is_company: isCompany });
    return data;
  }

  /**
   * Per-company toggle: allow the same email to belong to multiple
   * user accounts inside this company's sub-tenant tree.
   *
   * Backend enforces that the target tenant must already be flagged
   * `is_company=true` — otherwise responds 400.
   */
  async setTenantMultiUserLogin(tenantId: string, allowed: boolean): Promise<{ multi_user_login_allowed: boolean }> {
    const { data } = await api.post(`/super-admin/tenants/${tenantId}/set-multi-user-login`, { allowed });
    return data;
  }

  /**
   * Delete a tenant
   */
  async deleteTenant(tenantId: string): Promise<void> {
    await api.delete(`/super-admin/tenants/${tenantId}`);
  }

  /**
   * Get users in a specific tenant
   */
  async getTenantUsers(tenantId: string): Promise<User[]> {
    const { data } = await api.get(`/super-admin/tenants/${tenantId}/users`);
    return data.users || [];
  }

  /**
   * Get all conversation types (for tenant editor)
   */
  async getAllConversationTypes(): Promise<Array<{
    type: string;
    name: string;
    description: string;
    supports_model_switching: boolean;
    supports_file_attachments: boolean;
    supports_system_prompts: boolean;
    allowed_models: string | string[];
    required_config?: string[];
  }>> {
    const { data } = await api.get('/super-admin/conversation-types');
    return data.conversation_types;
  }

  // ===== User Management =====

  /**
   * Get all users across all tenants
   */
  async getUsers(params?: {
    page?: number;
    per_page?: number;
    role?: string;
    status?: string;
    tenant?: string;
    search?: string;
  }): Promise<UsersResponse> {
    const { data } = await api.get('/super-admin/users', { params });
    return data;
  }

  /**
   * Create a user in a specific tenant
   */
  async createUser(payload: CreateUserPayload): Promise<User> {
    const { data } = await api.post('/super-admin/users', payload);
    return data.user;
  }

  /**
   * Update user information
   */
  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    const { data } = await api.put(`/super-admin/users/${userId}`, updates);
    return data.user;
  }

  /**
   * Delete a user
   */
  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/super-admin/users/${userId}`);
  }

  /**
   * Batch upload users from JSON data
   */
  async batchUploadUsers(users: BatchUserUpload[]): Promise<BatchUploadResult> {
    const { data } = await api.post('/super-admin/users/batch-upload', users);
    return data;
  }

  // ===== Analytics =====

  /**
   * Get platform analytics
   */
  async getPlatformAnalytics(period: string = 'month'): Promise<PlatformAnalytics> {
    const { data } = await api.get('/super-admin/analytics', {
      params: { period },
    });
    return data;
  }

  // ===== Tenant Context =====

  async getTenantContextPrompt(tenantId: string): Promise<{ tenant_id: string; tenant_name: string; context_prompt: string | null; files_context_prompt: string | null }> {
    const { data } = await api.get(`/super-admin/tenants/${tenantId}/context-prompt`);
    return data;
  }

  async updateTenantContextPrompt(tenantId: string, contextPrompt: string): Promise<void> {
    await api.put(`/super-admin/tenants/${tenantId}/context-prompt`, { context_prompt: contextPrompt });
  }

  async previewTenantContext(tenantId: string): Promise<TenantContextPreviewResponse> {
    const { data } = await api.get(`/super-admin/tenants/${tenantId}/context-prompt/preview`);
    return data;
  }

  async listTenantContextFiles(tenantId: string): Promise<any> {
    const { data } = await api.get(`/super-admin/tenants/${tenantId}/context-files`);
    return data;
  }

  async uploadTenantContextFile(tenantId: string, file: File): Promise<any> {
    const CHUNK_SIZE = 20 * 1024 * 1024; // 20 MB
    const SA_URL = '/super-admin/tenants';

    // Small files: direct upload
    if (file.size <= 30 * 1024 * 1024) {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`${SA_URL}/${tenantId}/context-files`, formData, { timeout: 300_000 });
      return data;
    }

    // Large files: chunked upload
    const uploadId = crypto.randomUUID();
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('file', chunk, file.name);
      formData.append('upload_id', uploadId);
      formData.append('chunk_index', String(i));
      formData.append('total_chunks', String(totalChunks));

      await api.post(`${SA_URL}/${tenantId}/context-files/upload-chunk`, formData, { timeout: 120_000 });
    }

    const { data } = await api.post(
      `${SA_URL}/${tenantId}/context-files/complete-chunked`,
      {
        upload_id: uploadId,
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        total_chunks: totalChunks,
      },
      { timeout: 300_000 },
    );
    return data;
  }

  async deleteTenantContextFile(tenantId: string, attachmentId: string): Promise<void> {
    await api.delete(`/super-admin/tenants/${tenantId}/context-files/${attachmentId}`);
  }

  async updateTenantContextFile(tenantId: string, attachmentId: string, data: { include_as_context?: boolean }): Promise<any> {
    const { data: result } = await api.patch(`/super-admin/tenants/${tenantId}/context-files/${attachmentId}`, data);
    return result;
  }

  async addTenantCloudFiles(tenantId: string, provider: string, fileIds: string[]): Promise<any> {
    const { data: result } = await api.post(`/super-admin/tenants/${tenantId}/context-files/cloud`, {
      provider,
      file_ids: fileIds,
    });
    return result;
  }

  async listTenantDataBundles(tenantId: string): Promise<any> {
    const { data } = await api.get(`/super-admin/tenants/${tenantId}/data-bundles`);
    return data;
  }

  async updateTenantDataBundles(tenantId: string, bundleIds: string[]): Promise<any> {
    const { data } = await api.put(`/super-admin/tenants/${tenantId}/data-bundles`, { bundle_ids: bundleIds });
    return data;
  }

  async updateTenantDataBundle(tenantId: string, bundleId: string, data: { include_as_context: boolean }): Promise<void> {
    await api.patch(`/super-admin/tenants/${tenantId}/data-bundles/${bundleId}`, data);
  }

  async removeTenantDataBundle(tenantId: string, bundleId: string): Promise<void> {
    await api.delete(`/super-admin/tenants/${tenantId}/data-bundles/${bundleId}`);
  }

  async updateTenantFilesContextPrompt(tenantId: string, prompt: string): Promise<void> {
    await api.put(`/super-admin/tenants/${tenantId}/files-context-prompt`, { files_context_prompt: prompt });
  }

  async regenerateTenantContext(tenantId: string): Promise<{ success: boolean; message: string }> {
    const { data } = await api.post(`/super-admin/tenants/${tenantId}/regenerate-context`);
    return data;
  }

  // ===== System Prompts Management =====

  /**
   * Get all system prompts
   */
  async getSystemPrompts(): Promise<SystemPromptsResponse> {
    const { data } = await api.get('/super-admin/system-prompts');
    return data;
  }

  /**
   * Get a specific system prompt
   */
  async getSystemPrompt(promptId: string): Promise<SystemPrompt> {
    const { data } = await api.get(`/super-admin/system-prompts/${promptId}`);
    return data.system_prompt;
  }

  /**
   * Get the default system prompt
   */
  async getDefaultSystemPrompt(): Promise<SystemPrompt> {
    const { data } = await api.get('/super-admin/system-prompts/default');
    return data.system_prompt;
  }

  /**
   * Create a new system prompt
   */
  async createSystemPrompt(payload: CreateSystemPromptPayload): Promise<SystemPrompt> {
    const { data } = await api.post('/super-admin/system-prompts', payload);
    return data.system_prompt;
  }

  /**
   * Update an existing system prompt
   */
  async updateSystemPrompt(
    promptId: string,
    payload: UpdateSystemPromptPayload
  ): Promise<SystemPrompt> {
    const { data } = await api.put(`/super-admin/system-prompts/${promptId}`, payload);
    return data.system_prompt;
  }

  /**
   * Delete a system prompt
   */
  async deleteSystemPrompt(promptId: string): Promise<void> {
    await api.delete(`/super-admin/system-prompts/${promptId}`);
  }

  /**
   * Get system prompt assigned to a tenant
   */
  async getTenantSystemPrompt(tenantId: string): Promise<SystemPrompt | null> {
    try {
      const { data } = await api.get(`/super-admin/tenants/${tenantId}/system-prompt`);
      return data.system_prompt;
    } catch (error) {
      return null;
    }
  }

  /**
   * Assign a system prompt to a tenant
   */
  async assignSystemPromptToTenant(tenantId: string, promptId: string): Promise<void> {
    await api.post(`/super-admin/tenants/${tenantId}/system-prompt`, { prompt_id: promptId });
  }

  /**
   * Remove system prompt assignment from a tenant
   */
  async removeTenantSystemPrompt(tenantId: string): Promise<void> {
    await api.delete(`/super-admin/tenants/${tenantId}/system-prompt`);
  }
}

// Create and export singleton instance
const superAdminService = new SuperAdminService();

export default superAdminService;
export type {
  PlatformStats,
  Tenant,
  TenantsResponse,
  User,
  UsersResponse,
  BatchUserUpload,
  BatchUploadResult,
  SystemPrompt,
  SystemPromptsResponse,
  CreateSystemPromptPayload,
  UpdateSystemPromptPayload,
};
